import { __rest, __awaiter } from '../node_modules/tslib/tslib.es6.js';
import React__default, { useState, useRef, useCallback, useEffect } from 'react';
import { retryMessage } from './api.js';
import { useBrandAppConfig } from '../api/cache/appConfigQueryOptions.js';
import { getHeadersWithUniqueVisitorId } from '../Auth/index.js';
import { Toaster, toast } from 'sonner';
import { ConciergeChatAreaV2 } from './components/ConciergeChatAreaV2.js';
import { ConciergeWidgetHeader } from './ConciergeWidgetHeader.js';
import { useAutoScroll } from './hooks/useAutoScroll.js';
import { useConversationLoader } from './hooks/useConversationLoader.js';
import { useStreamingSearch } from './hooks/useStreamingSearch.js';
import { useConciergeContext } from './ConciergeContextProvider.js';
import { usePageContext } from './hooks/usePageContext.js';
import { wrapSearchWithProductContext } from './utils/wrapSearchWithProductContext.js';
import { useConciergePendingQuery } from './stores/useConciergePendingQuery.js';

const ConciergeWidgetChatContent = ({ pageContext: providedPageContext, widgetState, showMaximize = true, isMaximized = false, onMaximize, onMinimize, title, }) => {
    // Get conversation state from context (includes apiBaseUrl for checkout-shop-app)
    const _a = useConciergeContext(), { apiBaseUrl } = _a, conversationState = __rest(_a, ["apiBaseUrl"]);
    // Brand config
    const { data: config } = useBrandAppConfig(apiBaseUrl);
    const params = new URLSearchParams(window.location.search);
    const brandId = params.get('brandId') || null;
    const appConfigId = params.get('appConfigId') || null;
    // Input state
    const [searchInputValue, setSearchInputValue] = useState('');
    // Get page context - use provided one or fallback to hook
    // usePageContext will work even without react-router (uses window.location)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const hookPageContext = usePageContext();
    const pageContext = providedPageContext !== null && providedPageContext !== void 0 ? providedPageContext : hookPageContext;
    // Get widget state and pending query
    const { isOpen } = widgetState;
    const { pendingQuery, pendingStacklineSku, pendingQueryId, clearPendingQuery } = useConciergePendingQuery();
    // Ref to track the last processed query ID to prevent duplicate sends
    const lastProcessedQueryIdRef = useRef(null);
    // Auto-scroll behavior
    const { chatWrapperRef, messagesEndRef, shouldScrollOnNextMessageRef, scrollToBottom } = useAutoScroll(conversationState.conversationHistory.length, conversationState.isActivelyStreaming, conversationState.streamingAIResponse, conversationState.conversationHistory.length === 0);
    // Streaming search - expose performSearch for direct calls from pending query handler
    const { performSearch, cancelStream } = useStreamingSearch({
        brandId,
        appConfigId,
        conversationId: conversationState.conversationId,
        setConversationId: conversationState.setConversationId,
        setIsActivelyStreaming: conversationState.setIsActivelyStreaming,
        setStreamingAIResponse: conversationState.setStreamingAIResponse,
        setCurrentStatus: conversationState.setCurrentStatus,
        setConversationHistory: conversationState.setConversationHistory,
        setIsWaitingForFollowUpQueries: conversationState.setIsWaitingForFollowUpQueries,
        setStreamingProductGroups: conversationState.setStreamingProductGroups,
        setProductMetadataMap: conversationState.setProductMetadataMap,
        setPreviewSearchQueries: conversationState.setPreviewSearchQueries,
        setPreviewQueryProducts: conversationState.setPreviewQueryProducts,
        setFollowUpQuestions: conversationState.setFollowUpQuestions,
        setStreamingFeaturedProduct: conversationState.setStreamingFeaturedProduct,
        scrollToBottom,
        shouldScrollOnNextMessageRef,
        apiBaseUrl,
    });
    // Handle search submission
    const handleSearch = useCallback((searchTerm, imageData, imageMimeType) => __awaiter(void 0, void 0, void 0, function* () {
        setSearchInputValue('');
        // Wrap search term with product context if on a PDP
        const searchTermWithContext = wrapSearchWithProductContext(searchTerm, pageContext);
        yield performSearch(searchTermWithContext, imageData, imageMimeType);
    }), [pageContext, performSearch]);
    // Handle retry message
    const handleRetry = (messageIndex) => __awaiter(void 0, void 0, void 0, function* () {
        if (!conversationState.conversationId) {
            toast.error('No conversation ID available');
            return;
        }
        try {
            // Remove messages from the message index onward
            conversationState.setConversationHistory((prev) => prev.slice(0, messageIndex));
            // Set streaming state
            conversationState.setIsActivelyStreaming(true);
            conversationState.setStreamingAIResponse('');
            conversationState.setCurrentStatus('');
            // Get headers
            const headers = yield getHeadersWithUniqueVisitorId();
            // Call retry API and stream the response
            yield retryMessage({
                conversationId: conversationState.conversationId,
                messageIndex,
                headersToInject: headers,
                onChunk: (chunk) => {
                    conversationState.setStreamingAIResponse((prev) => prev + chunk);
                },
                onEvent: (event) => {
                    if (event.type === 'follow_up_queries') {
                        conversationState.setFollowUpQuestions(event.data || []);
                        conversationState.setIsWaitingForFollowUpQueries(false);
                    }
                },
                onStatusChange: (status) => {
                    conversationState.setCurrentStatus(status);
                },
                onComplete: () => {
                    // Finalize the streaming message
                    const streamedContent = conversationState.streamingAIResponse;
                    if (streamedContent) {
                        conversationState.setConversationHistory((prev) => [
                            ...prev,
                            {
                                role: 'assistant',
                                content: streamedContent,
                                timestamp: new Date(),
                            },
                        ]);
                    }
                    // Reset streaming state
                    conversationState.setStreamingAIResponse('');
                    conversationState.setIsActivelyStreaming(false);
                    conversationState.setCurrentStatus('');
                },
                onError: (error) => {
                    console.error('Retry failed:', error);
                    toast.error('Failed to retry message');
                    conversationState.setIsActivelyStreaming(false);
                    conversationState.setStreamingAIResponse('');
                },
                onConversationId: (id) => {
                    // Conversation ID should already be set, but update if needed
                    if (id !== conversationState.conversationId) {
                        conversationState.setConversationId(id);
                    }
                },
                onNewProductMetadata: (products) => {
                    // Add products to the map
                    const newMap = new Map(conversationState.productMetadataMap);
                    products.forEach((product) => {
                        newMap.set(product.stacklineSku, product);
                    });
                    conversationState.setProductMetadataMap(newMap);
                },
                onFeaturedProduct: (product, question) => {
                    conversationState.setStreamingFeaturedProduct({ product, question });
                },
            });
        }
        catch (error) {
            console.error('Error retrying message:', error);
            toast.error('Failed to retry message');
            conversationState.setIsActivelyStreaming(false);
        }
    });
    // Handle update feedback
    const handleUpdateFeedback = (messageIndex, feedback) => {
        // Update the feedback in the conversation history
        conversationState.setConversationHistory((prev) => {
            return prev.map((msg, idx) => {
                if (idx === messageIndex) {
                    return Object.assign(Object.assign({}, msg), { feedback: feedback || undefined });
                }
                return msg;
            });
        });
    };
    // Conversation loader
    const { isLoadingConversationQuery } = useConversationLoader({
        conversationId: conversationState.conversationId,
        isActivelyStreaming: conversationState.isActivelyStreaming,
        conversationHistoryLength: conversationState.conversationHistory.length,
        apiBaseUrl,
        onConversationLoaded: (messages, productMap, followUpQuestions, previewSearchQueries, previewQueryProducts) => {
            conversationState.setProductMetadataMap(productMap);
            conversationState.setConversationHistory(messages);
            conversationState.setFollowUpQuestions(followUpQuestions);
            conversationState.setPreviewSearchQueries(previewSearchQueries);
            conversationState.setPreviewQueryProducts(previewQueryProducts);
        },
        onConversationNotFound: () => {
            conversationState.setConversationId(null);
        },
    });
    // Sync conversationId with URL (disabled in widget)
    // useUrlSync(conversationState.conversationId);
    // Auto-send from URL params (disabled in widget)
    // useAutoSendFromUrl({
    //   isActivelyStreaming: conversationState.isActivelyStreaming,
    //   conversationLength: conversationState.conversationHistory.length,
    //   onSearch: handleSearch,
    // });
    // Handle pending queries from PDP conversation starters
    // This effect runs when: widget opens, or a new pending query is set
    useEffect(() => {
        // Skip if no pending query or widget not open
        if (!pendingQuery || !pendingQueryId || !isOpen) {
            return;
        }
        // Skip if we already processed this exact query (by ID)
        if (lastProcessedQueryIdRef.current === pendingQueryId) {
            return;
        }
        // Mark as processed immediately to prevent re-processing
        lastProcessedQueryIdRef.current = pendingQueryId;
        // Capture values before clearing
        const queryToSend = pendingQuery;
        const stacklineSkuToUse = pendingStacklineSku;
        // Clear the pending query immediately
        clearPendingQuery();
        // Wrap query with product context if we have a stacklineSku
        let messageToSend = queryToSend;
        if (stacklineSkuToUse) {
            const productPageContext = {
                pageType: 'pdp',
                stacklineSku: stacklineSkuToUse,
            };
            messageToSend = wrapSearchWithProductContext(queryToSend, productPageContext);
        }
        // Send the message - performSearch handles adding user message and streaming response
        performSearch(messageToSend);
    }, [isOpen, pendingQuery, pendingQueryId, pendingStacklineSku, performSearch, clearPendingQuery]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Toaster, null),
        React__default.createElement("div", { className: "concierge-root", style: {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                maxHeight: '100%',
                overflow: 'hidden',
            } },
            React__default.createElement(ConciergeWidgetHeader, { showMaximize: showMaximize, isMaximized: isMaximized, onMaximize: onMaximize, onMinimize: onMinimize, onClose: widgetState.close, title: title }),
            React__default.createElement(ConciergeChatAreaV2, { appName: config === null || config === void 0 ? void 0 : config.appName, brandId: brandId, conversationId: conversationState.conversationId, isEmbedded: true, apiBaseUrl: apiBaseUrl, conversationHistory: conversationState.conversationHistory, productMetadataMap: conversationState.productMetadataMap, streamingProductGroups: conversationState.streamingProductGroups, previewSearchQueries: conversationState.previewSearchQueries, previewQueryProducts: conversationState.previewQueryProducts, followUpQuestions: conversationState.followUpQuestions, streamingFeaturedProduct: conversationState.streamingFeaturedProduct, isActivelyStreaming: conversationState.isActivelyStreaming, streamingAIResponse: conversationState.streamingAIResponse, currentStatus: conversationState.currentStatus, isWaitingForFollowUpQueries: conversationState.isWaitingForFollowUpQueries, isLoadingConversationQuery: isLoadingConversationQuery, handleSearch: handleSearch, onRetry: handleRetry, onUpdateFeedback: handleUpdateFeedback, onStop: cancelStream, searchInputValue: searchInputValue, setSearchInputValue: setSearchInputValue }))));
};
// ConciergeWidgetChat expects to be inside a QueryClientProvider (provided by ConciergeStandalone)
const ConciergeWidgetChat = (props) => {
    return React__default.createElement(ConciergeWidgetChatContent, Object.assign({}, props));
};

export { ConciergeWidgetChat };
//# sourceMappingURL=ConciergeWidgetChat.js.map
